import type { NextPage } from 'next'
import Image from 'next/image'

import {
    accountDetailsSwitchImage,
    activityStreamImage,
    gregorFromPersonioImage,
    personioLogo,
    philipFromVaiTradeImage,
    pipelineTableVideo,
    stackCardsVideo,
    tableCloseDatesImage,
    todayStackImage,
    marblesDistributedOnPathsImage,
    vaiLogo,
    marblesDistributedOnPathsVideo,
} from 'src/libs/assets'
import { PageLayout } from 'src/libs/layout'
import {
    IntroSection,
    PropositionSection,
    SecuritySection,
    ClosingSection,
    ThreeCardsSection,
    FaqSection,
    Testimonial,
    BannerWithCtaSection,
    RequestDemoButton,
} from 'src/libs/ui-shared'
import { LazyVideo } from 'src/libs/react-utils'

const TeamLeadsSolutionPage: NextPage = () => {
    return (
        <PageLayout isNavBarFixed>
            <div className="grid gap-40">
                <IntroSection
                    video={marblesDistributedOnPathsVideo}
                    image={marblesDistributedOnPathsImage}
                    imageAlt="Tube distributing marbles onto different paths"
                    label="Team Leads"
                    title="Coordinate your team hands-on"
                    description="Laserfocus simplifies your whole sales process, so your team doesn’t need to bother with multiple tools and workflows. So they can entirely focus on closing deals."
                >
                    <div className="grid justify-items-center md:justify-items-start grid-cols-1">
                        <RequestDemoButton />
                    </div>
                </IntroSection>

                <PropositionSection
                    title="Your team spends time on things that matter"
                    description="Laserfocus automatically segments the most important prospects and deals for your team. Help them spend their time so everyone can hit their goals."
                    image={<LazyVideo {...stackCardsVideo} />}
                    imageShadow
                    swapElementsWhenVertical
                >
                    <Testimonial
                        companyLogo={vaiLogo}
                        companyName="VAI"
                        description="“My team increased productivity by 25% - more calls, more deals.”"
                        authorImage={philipFromVaiTradeImage}
                        authorName="Philip"
                        authorInfo="VP Sales from Vai Trade"
                    />
                </PropositionSection>

                <PropositionSection
                    title="Provide a healthy pipeline to everyone"
                    description="Never lose deals stall due to inactivity or parked leads. Redistribute leads, tasks and opportunities in an instant to keep the momentum."
                    image={<LazyVideo {...pipelineTableVideo} />}
                    imageShadow
                >
                    <Testimonial
                        companyLogo={personioLogo}
                        companyName="Personio"
                        description="“I am in love. My pipe is up to date for the first time.”"
                        authorImage={gregorFromPersonioImage}
                        authorName="Gregor"
                        authorInfo="Account Executive from Personio"
                    />
                </PropositionSection>

                <PropositionSection
                    title="Better 1-on-1s for everyone"
                    description="Be a partner for your team by understanding each deal as they do. Get an overview of what blocks them in an instant and provide your guidance."
                    image={
                        <Image
                            src={accountDetailsSwitchImage}
                            alt="Switch between account details views"
                            layout="responsive"
                            placeholder="blur"
                        />
                    }
                    imageShadow
                    swapElementsWhenVertical
                />

                <BannerWithCtaSection />

                <SecuritySection />

                <ThreeCardsSection
                    title="Hand-over the micro management to Laserfocus"
                    description="Laserfocus will remind your team to keep their data updated, nudge them to work on important deals, and set reminders so nothing gets forgotten. You can see how all is working together automatically."
                    cards={[
                        {
                            image: (
                                <Image
                                    src={todayStackImage}
                                    alt="Daily overview in Laserfocus app"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Daily overview',
                            description:
                                'The dailies shows your team what is urgent and where they have to follow up. No need to remind them via Slack.',
                            backgroundColor: 'blue',
                        },
                        {
                            image: (
                                <Image
                                    src={tableCloseDatesImage}
                                    alt="Soon close dates table"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'What’s missing',
                            description:
                                'Stacks in Laserfocus shows your team where close dates are overdue, which field information is missing, so you don’t have to.',
                            backgroundColor: 'purple',
                        },
                        {
                            image: (
                                <Image
                                    src={activityStreamImage}
                                    alt="Acitivty stream"
                                    layout="responsive"
                                    placeholder="blur"
                                />
                            ),
                            title: 'Understand the deals',
                            description:
                                'All activities are shown readable chronologically in Laserfocus. You will understand each deal as well as your sales agent at one glance.',
                            backgroundColor: 'red',
                        },
                    ]}
                />

                <FaqSection
                    title="Frequently asked questions"
                    description="Laserfocus gives you insights into why your deals are stalling or your forcecasts are off, and no longer leaves you with surprises."
                    elements={[
                        {
                            question: 'How can I try Laserfocus for myself?',
                            answer: 'Connect with one click via Salesforce Oauth and start using Laserfocus. Laserfocus sits on top of your Salesforce data, so there is no setup or installation required.',
                        },
                        {
                            question: 'What does Laserfocus cost?',
                            answer: 'It’s free for individuals and small teams trying out Laserfocus.',
                        },
                        {
                            question: 'Do I need my Salesforce Admin for setup?',
                            answer: [
                                'Nope. Laserfocus doesn’t require any setup or installation.',
                                'You securely log in and connect via Salesforce Oauth with Laserfocus and can start using it instantly if your Salesforce license has API access.',
                            ],
                        },
                        {
                            question: 'How long does the setup need?',
                            answer: 'Like everything in Laserfocus: it’s superfast. One click and you are ready. Means 30 seconds.',
                        },
                        {
                            question: 'Are you changing my Salesforce setup?',
                            answer: [
                                'No. Salesforce is defined and setup by your company, we don’t interact with your workflows in Salesforce or create any fields.',
                                'Everything stays untouched in your Salesforce instance.',
                            ],
                        },
                        {
                            question: 'How much time can Laserfocus save me?',
                            answer: 'Glad you asked. Just think about how much time you spend in Salesforce per day and cut that by 80%.',
                        },
                    ]}
                />

                <ClosingSection
                    title="Start Laserfocus now"
                    description="Simply explore Laserfocus for yourself or get in contact with us to see how you can remove all friction from your sales process."
                />
            </div>
        </PageLayout>
    )
}

export default TeamLeadsSolutionPage
